import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  AlertTriangle,
  Battery,
  Bell,
  ChevronLeft,
  GripVertical,
  Mail,
  MessageSquare,
  Pencil,
  Phone,
  PhoneCall,
  Plus,
  Signal,
  Sparkles,
  Trash2,
  Wifi,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProfile } from '@/api/profile'
import { useSmsFromNumbers } from '@/api/aiAgents'
import { cn } from '@/lib/utils'
import { formatHeure } from '@/lib/heure'
import {
  newNotifyAdminAction,
  notifyAdminActionSchema,
  slugifyFieldName,
  type CollectFieldType,
  type NotificationMethod,
  type NotifyAdminAction,
  type NotifyTiming,
} from './actionsSchemas'
import { CollapsibleSection } from './CollapsibleSection'
import {
  buildPresetAction,
  NOTIFY_PRESET_IDS,
  type NotifyPresetId,
} from './notifyAdminPresets'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSave: (action: NotifyAdminAction) => void
  action?: NotifyAdminAction | null
  /**
   * Langue de l'agent (fr/en/…) — distincte de la langue de l'interface.
   * Ce que l'agent DIT à l'appelant suit cette langue ; ce que l'admin LIT
   * (nom, libellés, message reçu) suit la langue de l'interface.
   */
  agentLanguage?: string
  /** Nécessaires pour lister les lignes de l'entreprise (destinataire SMS). */
  companyId?: number
  agentId?: number
}

/** Valeur sentinelle du select quand on saisit un numéro libre. */
const CUSTOM_PHONE = '__custom__'

/** Icône par situation, dans l'ordre d'affichage. */
const PRESET_ICONS: Record<NotifyPresetId, typeof Bell> = {
  take_message: MessageSquare,
  callback_request: PhoneCall,
  emergency: AlertTriangle,
  custom: Pencil,
}

const FIELD_TYPES: CollectFieldType[] = [
  'text',
  'phone',
  'email',
  'choice',
  'boolean',
  'number',
]

export function NotifyAdminModal({
  isOpen,
  onClose,
  onSave,
  action,
  agentLanguage,
  companyId,
  agentId,
}: Props) {
  const { t, i18n } = useTranslation()

  // Résolveur figé sur la langue de l'AGENT : sert uniquement aux textes que
  // l'appelant entend (réponse de l'agent) et que le LLM lit (scénario).
  const tAgent = i18n.getFixedT(
    (agentLanguage || '').toLowerCase().startsWith('en') ? 'en' : 'fr',
  )

  // Destinataire : le produit connaît déjà le compte connecté et les lignes
  // de l'entreprise, il n'y a aucune raison de les faire ressaisir.
  const { data: profile } = useProfile()
  const { data: companyLines = [] } = useSmsFromNumbers(companyId, agentId)
  const myPhone = profile?.phone_number || ''
  const myEmail = profile?.email || ''

  // À la création, on demande d'abord la situation : le formulaire n'apparaît
  // qu'ensuite, entièrement pré-rempli. En édition, on l'affiche directement.
  const [situationChosen, setSituationChosen] = useState(!!action)
  // Saisie libre d'un numéro (option « Autre numéro »).
  const [forcedCustom, setForcedCustom] = useState(false)

  const form = useForm<NotifyAdminAction>({
    resolver: zodResolver(notifyAdminActionSchema),
    defaultValues: action ?? newNotifyAdminAction(),
  })
  const { control, register, setValue, watch, handleSubmit, formState } = form

  const fields = useFieldArray({ control, name: 'collect_fields' })

  // Champ texte tampon pour les options du type 'choice', clé = index du champ.
  const [optionInputs, setOptionInputs] = useState<Record<number, string>>({})

  useEffect(() => {
    if (isOpen) {
      form.reset(action ?? newNotifyAdminAction())
      setOptionInputs({})
      setSituationChosen(!!action)
      setForcedCustom(false)
    }
  }, [isOpen, action, form])

  /** Applique une situation : remplit les 5 champs d'un coup. */
  const chooseSituation = (presetId: NotifyPresetId) => {
    form.reset(
      buildPresetAction(presetId, t, tAgent, {
        adminPhone: myPhone || companyLines[0]?.phone_number || '',
        adminEmail: myEmail,
      }),
    )
    setOptionInputs({})
    setSituationChosen(true)
  }

  const watchedFields = watch('collect_fields')
  const notificationMethod = watch('notification_method')
  const timing = watch('timing')
  const adminEmail = watch('admin_email')
  const adminPhone = watch('admin_phone')

  // Le select reflète `admin_phone`. `forcedCustom` est nécessaire car
  // choisir « Autre numéro » vide le champ : sans lui, l'option se
  // désélectionnerait aussitôt et l'input disparaîtrait sous le curseur.
  const knownNumbers = [myPhone, ...companyLines.map((l) => l.phone_number)].filter(Boolean)
  const isKnownNumber = !!adminPhone && knownNumbers.includes(adminPhone)
  const showCustomPhone = forcedCustom || (!!adminPhone && !isKnownNumber)
  const phoneSelectValue = showCustomPhone
    ? CUSTOM_PHONE
    : isKnownNumber
      ? adminPhone
      : ''

  // Résumé lisible du destinataire : évite d'ouvrir la section pour savoir
  // qui reçoit, par quel canal et à quel moment.
  const recipientLabel =
    companyLines.find((l) => l.phone_number === adminPhone)?.label ||
    (adminPhone === myPhone && myPhone
      ? `${t('manager.aiAgents.notifyAdminModal.myMobile')} · ${myPhone}`
      : adminPhone)
  const recipientSummary = [
    notificationMethod === 'email' ? adminEmail : recipientLabel,
    notificationMethod === 'both' && adminEmail ? adminEmail : null,
    t(`manager.aiAgents.notifyAdminModal.timingLabels.${timing}`),
  ]
    .filter(Boolean)
    .join(' · ')

  const handlePhoneSelect = (value: string) => {
    const custom = value === CUSTOM_PHONE
    setForcedCustom(custom)
    setValue('admin_phone', custom ? '' : value, { shouldDirty: true })
  }
  const messageTemplate = watch('message_template')

  const onSubmit = (values: NotifyAdminAction) => {
    onSave(values)
    onClose()
  }

  const errMsg = (key?: string) =>
    key ? t(`manager.aiAgents.errors.${key}`) : null

  // Quelle section porte une erreur ? Les sections repliées démontent leur
  // contenu : sans cela, une validation échouée dans une section fermée ne
  // s'afficherait nulle part et « Enregistrer » semblerait inerte. Les deux
  // cas qui arrivent vraiment : la situation libre (tout est vide) et un
  // profil sans mobile (`admin_phone` non pré-rempli).
  const { errors } = formState
  const sectionErrors = {
    basic: !!(errors.name || errors.scenario || errors.agent_response),
    fields: !!errors.collect_fields,
    delivery: !!(
      errors.admin_phone ||
      errors.admin_email ||
      errors.notification_method ||
      errors.timing
    ),
    template: !!errors.message_template,
  }

  const addOption = (fieldIndex: number) => {
    const value = (optionInputs[fieldIndex] ?? '').trim()
    if (!value) return
    const current = watchedFields[fieldIndex]?.options ?? []
    setValue(
      `collect_fields.${fieldIndex}.options`,
      [...current, value],
      { shouldDirty: true },
    )
    setOptionInputs((s) => ({ ...s, [fieldIndex]: '' }))
  }

  const removeOption = (fieldIndex: number, optionIndex: number) => {
    const current = watchedFields[fieldIndex]?.options ?? []
    setValue(
      `collect_fields.${fieldIndex}.options`,
      current.filter((_, i) => i !== optionIndex),
      { shouldDirty: true },
    )
  }

  // Slug auto à partir du nom — clé d'interpolation dans le template.
  const handleNameChange = (index: number, name: string) => {
    setValue(`collect_fields.${index}.name`, name, { shouldDirty: true })
    setValue(`collect_fields.${index}.id`, slugifyFieldName(name), {
      shouldDirty: true,
    })
  }

  const availableVariables = [
    ...watchedFields.map(
      (f) => `{${f.id || slugifyFieldName(f.name || 'field')}}`,
    ),
    '{call_time}',
    '{agent_name}',
    '{caller_phone}',
  ]

  // Échantillons canadiens, et valeurs plausibles par intention du champ :
  // l'aperçu doit montrer un vrai message reçu, pas les libellés des champs
  // (« Nom du client » à la place d'un nom ne prouve rien au client).
  const sampleForField = (f: { id?: string; name?: string; type: CollectFieldType; options?: string[] }) => {
    if (f.type === 'phone') return '514 555-0198'
    if (f.type === 'email') return 'marie.tremblay@courriel.ca'
    if (f.type === 'number') return '3'
    if (f.type === 'boolean') return t('common.yes', { defaultValue: 'Oui' })
    if (f.type === 'choice' && f.options?.length) return f.options[0]
    const slug = f.id || slugifyFieldName(f.name || '')
    return t(`manager.aiAgents.notifyPresets.samples.${slug}`, {
      defaultValue: 'Marie Tremblay',
    })
  }

  const sampleData: Record<string, string> = {
    call_time: new Date().toLocaleString(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'short',
    }),
    agent_name: t('manager.aiAgents.notifyPresets.samples.agent_name'),
    caller_phone: '514 555-0198',
    ...Object.fromEntries(
      watchedFields.map((f) => [
        f.id || slugifyFieldName(f.name || 'field'),
        sampleForField(f),
      ]),
    ),
  }

  const previewMessage = messageTemplate.replace(
    /\{(\w+)\}/g,
    (m, key) => sampleData[key] ?? m,
  )

  const showEmailPreview = notificationMethod === 'email'
  const hasRequiredFields = watchedFields.some((f) => f.required)

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[680px] lg:max-w-4xl xl:max-w-6xl p-0 gap-0 max-h-[90vh] overflow-hidden">
        <form
          // stopPropagation : sinon le submit du modal bubble dans la React
          // virtual tree (via Portal Radix) et déclenche aussi le submit du
          // form parent AIAgentFormView = update global agent non voulu.
          onSubmit={(e) => {
            e.stopPropagation()
            return handleSubmit(onSubmit)(e)
          }}
          className="flex flex-col max-h-[90vh]"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-500/10 dark:to-orange-500/10">
            <DialogTitle className="text-xl font-semibold flex items-center gap-2">
              <div className="p-2 bg-amber-100 dark:bg-amber-500/20 rounded-lg">
                <Bell className="h-5 w-5 text-amber-600" />
              </div>
              {action
                ? t('manager.aiAgents.notifyAdminModal.editTitle')
                : t('manager.aiAgents.notifyAdminModal.createTitle')}
            </DialogTitle>
          </DialogHeader>

          {/* Étape 0 — choix de la situation (création uniquement).
              Le client répond à « quand voulez-vous être prévenu ? » ; le
              preset remplit nom, scénario, réponse de l'agent, champs à
              collecter et template. Plus aucun champ vide ensuite. */}
          {!situationChosen ? (
            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('manager.aiAgents.notifyPresets.title')}
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                  {t('manager.aiAgents.notifyPresets.subtitle')}
                </p>
              </div>

              <div className="space-y-2">
                {NOTIFY_PRESET_IDS.map((presetId) => {
                  const Icon = PRESET_ICONS[presetId]
                  return (
                    <button
                      key={presetId}
                      type="button"
                      onClick={() => chooseSituation(presetId)}
                      className="w-full flex items-start gap-3 rounded-lg border border-gray-200 dark:border-slate-700 p-4 text-left transition-colors hover:border-amber-400 hover:bg-amber-50/60 dark:hover:bg-amber-500/10"
                    >
                      <Icon className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-600" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                          {t(`manager.aiAgents.notifyPresets.${presetId}.label`)}
                        </span>
                        <span className="block text-xs text-gray-600 dark:text-slate-400 mt-0.5">
                          {t(`manager.aiAgents.notifyPresets.${presetId}.hint`)}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="w-full"
              >
                {t('common.cancel')}
              </Button>
            </div>
          ) : (
          <div className="flex flex-1 min-h-0">
            {/* Panneau gauche — formulaire */}
            <div className="flex-1 flex flex-col min-w-0">
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                {/* Retour au choix de situation — création uniquement :
                    en édition, la situation d'origine n'a plus de sens. */}
                {!action && (
                  <button
                    type="button"
                    onClick={() => setSituationChosen(false)}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    {t('manager.aiAgents.notifyPresets.changeSituation')}
                  </button>
                )}

                {/* Ce que ça produit : la seule chose que le client doit
                    lire avant de valider. Tout le reste est replié. */}
                <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-4 space-y-3">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                      {t('manager.aiAgents.notifyAdminModal.willAsk')}
                    </div>
                    <ul className="mt-1.5 space-y-1">
                      {watchedFields.map((f, i) => (
                        <li
                          key={f.id || i}
                          className="text-sm text-gray-700 dark:text-slate-300 flex items-center gap-2"
                        >
                          <span className="w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                          {f.name || t('manager.aiAgents.notifyAdminModal.unnamedField')}
                          {!f.required && (
                            <span className="text-xs text-gray-400">
                              {t('manager.aiAgents.notifyAdminModal.optionalTag')}
                            </span>
                          )}
                        </li>
                      ))}
                      {watchedFields.length === 0 && (
                        <li className="text-sm text-gray-400 italic">
                          {t('manager.aiAgents.notifyAdminModal.noFields')}
                        </li>
                      )}
                    </ul>
                  </div>

                  <div className="pt-2 border-t border-gray-100 dark:border-slate-800">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                      {t('manager.aiAgents.notifyAdminModal.youWillBeNotified')}
                    </div>
                    <p className="mt-1 text-sm text-gray-700 dark:text-slate-300">
                      {recipientSummary}
                    </p>
                  </div>
                </div>

                {/* Section 1 — Informations de base */}
                <CollapsibleSection
                  title={t('manager.aiAgents.notifyAdminModal.basicInfo')}
                  summary={watch('name')}
                  forceOpen={sectionErrors.basic}
                >

                  <div>
                    <Label htmlFor="notify-name" className="text-sm font-medium">
                      {t('manager.aiAgents.notifyAdminModal.name')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="notify-name"
                      placeholder={t(
                        'manager.aiAgents.notifyAdminModal.namePlaceholder',
                      )}
                      className="mt-2"
                      {...register('name')}
                    />
                    {formState.errors.name && (
                      <p className="text-xs text-red-500 mt-1">
                        {errMsg(formState.errors.name.message)}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="notify-scenario" className="text-sm font-medium">
                      {t('manager.aiAgents.notifyAdminModal.scenario')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="notify-scenario"
                      placeholder={t(
                        'manager.aiAgents.notifyAdminModal.scenarioPlaceholder',
                      )}
                      className="mt-2 min-h-[80px] resize-y"
                      {...register('scenario')}
                    />
                    {formState.errors.scenario && (
                      <p className="text-xs text-red-500 mt-1">
                        {errMsg(formState.errors.scenario.message)}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="notify-response" className="text-sm font-medium">
                      {t('manager.aiAgents.notifyAdminModal.agentResponse')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="notify-response"
                      placeholder={t(
                        'manager.aiAgents.notifyAdminModal.agentResponsePlaceholder',
                      )}
                      className="mt-2 min-h-[80px] resize-y"
                      {...register('agent_response')}
                    />
                    {formState.errors.agent_response && (
                      <p className="text-xs text-red-500 mt-1">
                        {errMsg(formState.errors.agent_response.message)}
                      </p>
                    )}
                  </div>
                </CollapsibleSection>

                {/* Section 2 — Champs à collecter */}
                <CollapsibleSection
                  title={t('manager.aiAgents.notifyAdminModal.collectFields')}
                  summary={t('manager.aiAgents.notifyAdminModal.fieldCount', {
                    count: watchedFields.length,
                  })}
                  forceOpen={sectionErrors.fields}
                >
                  <div className="flex items-center justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        fields.append({
                          id: `field_${Date.now()}`,
                          name: '',
                          type: 'text',
                          required: false,
                        })
                      }
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t('manager.aiAgents.notifyAdminModal.addField')}
                    </Button>
                  </div>

                  {fields.fields.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 dark:bg-slate-800/50 rounded-lg border-2 border-dashed border-gray-200 dark:border-slate-700">
                      <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {t('manager.aiAgents.notifyAdminModal.noFields')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {t('manager.aiAgents.notifyAdminModal.noFieldsHelp')}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {fields.fields.map((field, index) => {
                        const fieldType = watchedFields[index]?.type
                        const fieldOptions = watchedFields[index]?.options ?? []
                        return (
                          <div
                            key={field.id}
                            className="border dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900 hover:border-blue-200 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="text-gray-300 cursor-grab pt-2">
                                <GripVertical className="h-5 w-5" />
                              </div>

                              <div className="flex-1 space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs text-gray-500">
                                      {t(
                                        'manager.aiAgents.notifyAdminModal.fieldName',
                                      )}
                                    </Label>
                                    <Input
                                      placeholder={t(
                                        'manager.aiAgents.notifyAdminModal.fieldNamePlaceholder',
                                      )}
                                      value={watchedFields[index]?.name ?? ''}
                                      onChange={(e) =>
                                        handleNameChange(index, e.target.value)
                                      }
                                      className="mt-1"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-gray-500">
                                      {t(
                                        'manager.aiAgents.notifyAdminModal.fieldType',
                                      )}
                                    </Label>
                                    <Controller
                                      control={control}
                                      name={`collect_fields.${index}.type`}
                                      render={({ field: tField }) => (
                                        <Select
                                          value={tField.value}
                                          onValueChange={(v) =>
                                            tField.onChange(v as CollectFieldType)
                                          }
                                        >
                                          <SelectTrigger className="mt-1">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {FIELD_TYPES.map((typeKey) => (
                                              <SelectItem
                                                key={typeKey}
                                                value={typeKey}
                                              >
                                                {t(
                                                  `manager.aiAgents.notifyAdminModal.types.${typeKey}`,
                                                )}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      )}
                                    />
                                  </div>
                                </div>

                                {fieldType === 'choice' && (
                                  <div>
                                    <Label className="text-xs text-gray-500">
                                      {t(
                                        'manager.aiAgents.notifyAdminModal.options',
                                      )}
                                    </Label>
                                    <div className="flex gap-2 mt-1">
                                      <Input
                                        placeholder={t(
                                          'manager.aiAgents.notifyAdminModal.optionPlaceholder',
                                        )}
                                        value={optionInputs[index] ?? ''}
                                        onChange={(e) =>
                                          setOptionInputs((s) => ({
                                            ...s,
                                            [index]: e.target.value,
                                          }))
                                        }
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            e.preventDefault()
                                            addOption(index)
                                          }
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => addOption(index)}
                                      >
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    {fieldOptions.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mt-2">
                                        {fieldOptions.map((option, optIndex) => (
                                          <span
                                            key={`${index}-opt-${optIndex}`}
                                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-slate-800 rounded-md text-sm"
                                          >
                                            {option}
                                            <button
                                              type="button"
                                              onClick={() => removeOption(index, optIndex)}
                                              className="text-gray-400 hover:text-red-500"
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}

                                <div className="flex items-center gap-4">
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      checked={!!watchedFields[index]?.required}
                                      onChange={() =>
                                        setValue(
                                          `collect_fields.${index}.required`,
                                          true,
                                          { shouldDirty: true },
                                        )
                                      }
                                      className="text-blue-600"
                                    />
                                    <span className="text-sm font-medium text-red-600">
                                      {t(
                                        'manager.aiAgents.notifyAdminModal.required',
                                      )}
                                    </span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="radio"
                                      checked={!watchedFields[index]?.required}
                                      onChange={() =>
                                        setValue(
                                          `collect_fields.${index}.required`,
                                          false,
                                          { shouldDirty: true },
                                        )
                                      }
                                      className="text-blue-600"
                                    />
                                    <span className="text-sm text-gray-600 dark:text-slate-400">
                                      {t(
                                        'manager.aiAgents.notifyAdminModal.optional',
                                      )}
                                    </span>
                                  </label>
                                </div>
                              </div>

                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => fields.remove(index)}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {hasRequiredFields && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-500/10 rounded-lg border border-blue-200 dark:border-blue-500/30 flex items-start gap-3">
                      <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
                          {t('manager.aiAgents.notifyAdminModal.autoValidation')}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-300 mt-0.5">
                          {t('manager.aiAgents.notifyAdminModal.autoValidationHelp')}
                        </p>
                      </div>
                    </div>
                  )}
                </CollapsibleSection>

                {/* Section 3 — Configuration de notification */}
                <CollapsibleSection
                  title={t('manager.aiAgents.notifyAdminModal.deliverySection')}
                  summary={recipientSummary}
                  forceOpen={sectionErrors.delivery}
                >

                  <div>
                    <Label className="text-sm font-medium">
                      {t('manager.aiAgents.notifyAdminModal.method')}
                    </Label>
                    <div className="grid grid-cols-3 gap-3 mt-2">
                      {(
                        [
                          { value: 'sms', icon: Phone },
                          { value: 'email', icon: Mail },
                          { value: 'both', icon: Bell },
                        ] as Array<{ value: NotificationMethod; icon: typeof Phone }>
                      ).map(({ value, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() =>
                            setValue('notification_method', value, {
                              shouldDirty: true,
                            })
                          }
                          className={cn(
                            'flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all',
                            notificationMethod === value
                              ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-500/10'
                              : 'border-gray-200 dark:border-slate-700 hover:border-gray-300',
                          )}
                        >
                          <Icon className="h-5 w-5" />
                          <span className="text-sm font-medium">
                            {t(
                              `manager.aiAgents.notifyAdminModal.methodLabels.${value}`,
                            )}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {(notificationMethod === 'sms' || notificationMethod === 'both') && (
                    <div>
                      <Label className="text-sm font-medium">
                        {t('manager.aiAgents.notifyAdminModal.receivingLine')}
                      </Label>
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                        {t('manager.aiAgents.notifyAdminModal.receivingLineHint')}
                      </p>
                      {/* Un SMS arrive sur une LIGNE, pas sur une personne :
                          on propose les lignes de l'entreprise (avec leur
                          label), le mobile du compte connecté, et la saisie
                          libre. Le champ stocké reste `admin_phone`. */}
                      <Select
                        value={phoneSelectValue}
                        onValueChange={handlePhoneSelect}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue
                            placeholder={t('manager.aiAgents.notifyAdminModal.pickLine')}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {myPhone && (
                            <SelectGroup>
                              <SelectLabel>
                                {t('manager.aiAgents.notifyAdminModal.groupYou')}
                              </SelectLabel>
                              <SelectItem value={myPhone}>
                                {t('manager.aiAgents.notifyAdminModal.myMobile')} · {myPhone}
                              </SelectItem>
                            </SelectGroup>
                          )}
                          {companyLines.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>
                                {t('manager.aiAgents.notifyAdminModal.groupCompanyLines')}
                              </SelectLabel>
                              {companyLines.map((line) => (
                                <SelectItem
                                  key={line.phone_number}
                                  value={line.phone_number}
                                >
                                  {line.label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          <SelectItem value={CUSTOM_PHONE}>
                            {t('manager.aiAgents.notifyAdminModal.otherNumber')}
                          </SelectItem>
                        </SelectContent>
                      </Select>

                      {showCustomPhone && (
                        <Input
                          id="admin-phone"
                          placeholder="+1 514 555 1234"
                          className="mt-2"
                          {...register('admin_phone')}
                        />
                      )}
                      {formState.errors.admin_phone && (
                        <p className="text-xs text-red-500 mt-1">
                          {errMsg(formState.errors.admin_phone.message)}
                        </p>
                      )}
                    </div>
                  )}

                  {(notificationMethod === 'email' || notificationMethod === 'both') && (
                    <div>
                      <Label htmlFor="admin-email" className="text-sm font-medium">
                        {t('manager.aiAgents.notifyAdminModal.adminEmail')}
                      </Label>
                      <Input
                        id="admin-email"
                        type="email"
                        placeholder="admin@entreprise.com"
                        className="mt-2"
                        {...register('admin_email')}
                      />
                      {formState.errors.admin_email && (
                        <p className="text-xs text-red-500 mt-1">
                          {errMsg(formState.errors.admin_email.message)}
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <Label className="text-sm font-medium">
                      {t('manager.aiAgents.notifyAdminModal.timing')}
                    </Label>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      {(['realtime', 'post_call'] as NotifyTiming[]).map((tm) => (
                        <button
                          key={tm}
                          type="button"
                          onClick={() => setValue('timing', tm, { shouldDirty: true })}
                          className={cn(
                            'p-4 rounded-lg border-2 text-left transition-all',
                            timing === tm
                              ? tm === 'realtime'
                                ? 'border-green-500 bg-green-50 dark:bg-green-500/10'
                                : 'border-purple-500 bg-purple-50 dark:bg-purple-500/10'
                              : 'border-gray-200 dark:border-slate-700 hover:border-gray-300',
                          )}
                        >
                          <div className="font-medium text-sm">
                            {t(`manager.aiAgents.notifyAdminModal.timingLabels.${tm}`)}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                            {t(`manager.aiAgents.notifyAdminModal.timingDesc.${tm}`)}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Section 4 — Template du message */}
                <CollapsibleSection
                  title={t('manager.aiAgents.notifyAdminModal.templateSection')}
                  forceOpen={sectionErrors.template}
                >

                  <div>
                    <Label htmlFor="notify-template" className="text-sm font-medium">
                      {t('manager.aiAgents.notifyAdminModal.template')}{' '}
                      <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 mb-2">
                      {t('manager.aiAgents.notifyAdminModal.templateHelp')}
                    </p>
                    <Textarea
                      id="notify-template"
                      placeholder={t(
                        'manager.aiAgents.notifyAdminModal.templatePlaceholder',
                      )}
                      className="min-h-[120px] resize-y font-mono text-sm"
                      {...register('message_template')}
                    />
                    {formState.errors.message_template && (
                      <p className="text-xs text-red-500 mt-1">
                        {errMsg(formState.errors.message_template.message)}
                      </p>
                    )}

                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-500/10 rounded-lg border border-amber-200 dark:border-amber-500/30">
                      <p className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-2">
                        {t('manager.aiAgents.notifyAdminModal.clickToInsert')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {availableVariables.map((variable, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() =>
                              setValue(
                                'message_template',
                                (messageTemplate ?? '') + variable,
                                { shouldDirty: true },
                              )
                            }
                            className="px-2 py-1 bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 rounded text-xs font-mono hover:bg-amber-200 transition-colors cursor-pointer"
                          >
                            {variable}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </CollapsibleSection>

              </div>

              <div className="px-6 py-4 border-t dark:border-slate-800 flex justify-end gap-3 bg-gray-50 dark:bg-slate-800/50">
                <Button type="button" variant="outline" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {action
                    ? t('manager.aiAgents.update')
                    : t('manager.aiAgents.notifyAdminModal.create')}
                </Button>
              </div>
            </div>

            {/* Panneau droit — preview (visible ≥ xl pour laisser respirer le formulaire) */}
            <div className="hidden xl:flex w-[340px] flex-shrink-0 bg-gradient-to-b from-slate-900 to-slate-800 items-center justify-center border-l border-slate-700 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage:
                    'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                  backgroundSize: '24px 24px',
                }}
              />
              <div className="absolute top-4 left-0 right-0 text-center">
                <span className="text-[11px] font-medium text-slate-400 uppercase tracking-widest">
                  {showEmailPreview
                    ? t('manager.aiAgents.notifyAdminModal.emailPreview')
                    : t('manager.aiAgents.notifyAdminModal.smsPreview')}
                </span>
              </div>
              {showEmailPreview ? (
                <EmailPreview
                  message={previewMessage}
                  timing={timing}
                  adminEmail={adminEmail ?? ''}
                />
              ) : (
                <PhonePreview message={previewMessage} timing={timing} />
              )}
            </div>
          </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Sous-composants previews (purs, ne dépendent que des props)
// =============================================================================

function PhonePreview({
  message,
  timing,
}: {
  message: string
  timing: NotifyTiming
}) {
  const { t, i18n } = useTranslation()
  const timeStr = formatHeure(new Date(), i18n.language)

  return (
    <div className="flex flex-col items-center justify-center h-full py-4">
      <div className="relative w-[280px] h-[560px] bg-black rounded-[3rem] p-[3px] shadow-2xl">
        <div className="relative w-full h-full bg-gray-50 rounded-[2.8rem] overflow-hidden flex flex-col">
          <div className="flex justify-center pt-2 pb-0">
            <div className="w-[90px] h-[28px] bg-black rounded-full" />
          </div>
          <div className="flex items-center justify-between px-6 py-1 text-[10px] font-semibold text-gray-800">
            <span>{timeStr}</span>
            <div className="flex items-center gap-1">
              <Signal className="w-3 h-3" />
              <Wifi className="w-3 h-3" />
              <Battery className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-white/80 backdrop-blur-sm">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
              C
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900 leading-tight">Callizy</p>
              <p className="text-[10px] text-gray-500">
                {t('manager.aiAgents.notifyAdminModal.aiAgentLabel')}
              </p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50">
            {!message ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <MessageSquare className="w-7 h-7 text-gray-300" />
                </div>
                <p className="text-xs font-medium text-gray-400">
                  {t('manager.aiAgents.notifyAdminModal.previewEmpty')}
                </p>
                <p className="text-[10px] text-gray-300 mt-1">
                  {t('manager.aiAgents.notifyAdminModal.previewEmptyHelp')}
                </p>
              </div>
            ) : (
              <>
                <div className="text-center">
                  <span className="text-[9px] text-gray-400 bg-gray-100/80 px-2 py-0.5 rounded-full">
                    {t('manager.aiAgents.notifyAdminModal.previewTimestamp', {
                      time: timeStr,
                    })}
                  </span>
                </div>
                <div className="flex justify-start">
                  <div className="max-w-[220px] px-3 py-2 rounded-2xl rounded-tl-md bg-white border border-gray-200 shadow-sm">
                    <p className="text-[12px] text-gray-800 whitespace-pre-line leading-relaxed">
                      {message}
                    </p>
                    {timing === 'post_call' && (
                      <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex items-center gap-1 mb-1">
                          <Sparkles className="w-3 h-3 text-purple-500" />
                          <span className="text-[10px] font-semibold text-purple-600">
                            {t('manager.aiAgents.notifyAdminModal.aiSummary')}
                          </span>
                        </div>
                        <p className="text-[11px] text-purple-700 leading-relaxed bg-purple-50 rounded-lg p-1.5">
                          {t('manager.aiAgents.notifyAdminModal.aiSummaryDemo')}
                        </p>
                      </div>
                    )}
                    <p className="text-[9px] text-gray-400 text-right mt-1">{timeStr}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function EmailPreview({
  message,
  timing,
  adminEmail,
}: {
  message: string
  timing: NotifyTiming
  adminEmail: string
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-center justify-center h-full py-4 px-4 w-full">
      <div className="w-full max-w-[320px] bg-white rounded-lg shadow-2xl overflow-hidden border border-gray-200">
        <div className="px-4 py-3 space-y-1.5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 w-8">
              {t('manager.aiAgents.notifyAdminModal.emailFrom')}
            </span>
            <span className="text-[11px] text-gray-700 font-medium">Callizy</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 w-8">
              {t('manager.aiAgents.notifyAdminModal.emailTo')}
            </span>
            <span className="text-[11px] text-gray-700">
              {adminEmail || 'admin@entreprise.com'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-gray-400 w-8">
              {t('manager.aiAgents.notifyAdminModal.emailSubject')}
            </span>
            <span className="text-[11px] text-gray-900 font-semibold">
              {t('manager.aiAgents.notifyAdminModal.emailSubjectDemo')}
            </span>
          </div>
        </div>
        <div className="px-4 py-3 min-h-[200px]">
          {!message ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Mail className="w-8 h-8 text-gray-200 mb-2" />
              <p className="text-[11px] text-gray-400">
                {t('manager.aiAgents.notifyAdminModal.previewEmpty')}
              </p>
            </div>
          ) : (
            <>
              <p className="text-[12px] text-gray-800 whitespace-pre-line leading-relaxed">
                {message}
              </p>
              {timing === 'post_call' && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1 mb-1.5">
                    <Sparkles className="w-3 h-3 text-purple-500" />
                    <span className="text-[10px] font-semibold text-purple-600">
                      {t('manager.aiAgents.notifyAdminModal.aiSummary')}
                    </span>
                  </div>
                  <p className="text-[11px] text-purple-700 leading-relaxed bg-purple-50 rounded-lg p-2.5 border border-purple-100">
                    {t('manager.aiAgents.notifyAdminModal.aiSummaryDemo')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <p className="text-[9px] text-gray-400 text-center">
            {t('manager.aiAgents.notifyAdminModal.emailFooter')}
          </p>
        </div>
      </div>
    </div>
  )
}
